import { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import CustomTable, { CustomColumnDef } from '../../../components/customTable/CustomTable'
import { useDispatch, useSelector } from 'react-redux'
import { selectExpandedRows, selectOptionChainColumns, selectOptionChainMirrorScroll, selectOptionChainTimeInterval, selectPrimarySecurity } from '../../../selectors/home'
import { getSecurityKey } from '../../../util/security'
import { selectUnderlyingOptionsExpiries, selectUnderlyingOptionsTradeData } from '../../../selectors/options'
import { Box, Paper, Typography, IconButton, Tooltip } from '@mui/material'
import { OptionExpiryType } from '../../../reducer/options.reducer'
import { toggleOptionChainExpandedRow } from '../../../reducer/home.reducer'
import { useResizeDetector } from 'react-resize-detector'
import { debounce } from 'throttle-debounce'
import { selectOptionChainTableView, selectOptionChainVisibleStrikes, selectShowOptionChainQuantityInLots } from '../../../selectors/common'
import { optionsTradesDataType, optionTradeDataType } from '../../typings/options.typings'
import OptionChainExpandedTable, { OptionChainExpandedTableProps } from './components/optionChainExpandedTable'
import { isOptionChainLiveView, OPTION_CHAIN_TABLE_ID, OptionChainViewKey } from '../../../assets/constants'
import Loading from '../../../components/common/Loading'
import ExpiryPcrRender from './components/ExpiryPcrRender'
import ExpiryColumnRender from './components/ExpiryColumnRender'
import useStyles from './styles'
import { fetchOptionChainV2 } from '../../../util/options'
import { GetOptionChainRequestV2 } from '../../../proto/generated/candle-data-depot-option-chain'
import { OptionChainTableView } from '../fnoHeader/optionsChainSettingsDialog'
import ExpiryRowRenderOnly from './components/ExpiryRowRenderOnly'
import PauseIcon from '../../../assets/icons/PauseIcon'
import MirrorIcon from '../../../assets/icons/MirrorIcon'
import commonActions from '../../../actions/common.actions'
import SubtleShimmer from '../../../components/common/SubtleShimmer'
import OptionChainSkeleton from './OptionChainSkeleton'
import { OptionChainPopoverProvider } from './components/OptionChainPopoverContext'
export const spanCols: string[] = []

export const getOptionChainCellIdentityKey = (columnValue: string, subColumn?: string) =>
    subColumn ? `${columnValue}|${subColumn}` : columnValue

export const colMinWidth: Record<string, number> = {
    ltp: 80,
    ltpChange: 80,
    bid: 80,
    ask: 80,
    volume: 100,
    impliedVolatility: 100,
    delta: 100,
    gamma: 100,
    vega: 100,
    theta: 100,
    rho: 100,
    openInterest: 125,
    'oiChange|outlook': 125,
}

export const colsWidth = (extended = false): Record<string, number> => ({
    outlook: 0.5,
    ltp: 1,
    ltpChange: 1,
    expiry: 1.5
})

interface OptionChainTableProps {
    strikeField: string;
    collapseType: string;
}

export const optionColumnOptions = [
    { value: 'ltp', label: 'LTP' },
    { value: 'ltpChange', label: 'Chg' },
    { value: 'bid', label: "Bid" },
    { value: 'ask', label: 'Ask' },
    { value: 'volume', label: 'Volume' },
    { value: 'impliedVolatility', label: 'Imp. Vol.' },
    { value: 'delta', label: 'Delta (δ)' },
    { value: 'gamma', label: 'Gamma (Γ)' },
    { value: 'vega', label: 'Vega (ν)' },
    { value: 'theta', label: 'Theta(Θ)' },
    { value: 'rho', label: 'Rho (ρ)' },
    { value: 'openInterest', label: 'OI' },
    { value: 'oiChange|outlook', label: 'OI Chg | Outlook' },
]

export const futureColumnOptions = [
    { value: 'expiry', label: 'Expiry', alwaysShow: true },
    { value: 'bid', label: 'Bid', alwaysShow: true },
    { value: 'ask', label: 'Ask', alwaysShow: true },
    { value: 'ltp', label: 'LTP' },
    { value: 'ltpChange', label: 'Chg' },
    { value: 'volume', label: 'Volume' },
    { value: 'openInterest', label: 'OI' },
    { value: 'oiChange|outlook', label: 'OI Chg | Outlook' },
]
interface Props {
    optionChainViewKey: OptionChainViewKey
    showSingleExpiry: boolean
    selectedOptionChainTableViewProp?: OptionChainTableView
    disableRowCollapse?: boolean
    /** Widget dashboard: read trade data for this underlying key instead of global primary. */
    underlyingToken?: string
}

const OptionChainTable = ({ optionChainViewKey, showSingleExpiry, selectedOptionChainTableViewProp = OptionChainTableView.NORMAL, disableRowCollapse = false, underlyingToken: underlyingTokenOverride }: Props) => {
    const dispatch = useDispatch()
    const { classes, cx } = useStyles()
    const { expressionMode, primary, underlying } = useSelector(selectPrimarySecurity)
    const security = expressionMode ? null : underlying || primary
    const token = underlyingTokenOverride || getSecurityKey(security?.exchange ?? 0, security?.code ?? '')
    const optionExpiries = useSelector(selectUnderlyingOptionsExpiries(token))
    const optionExpiriesData = useMemo(() => [] as optionsTradesDataType[], [])
    const optionExpiriesDataRef = useRef(optionExpiriesData)
    const leftScrollRef = useRef<HTMLDivElement>(null)
    const rightScrollRef = useRef<HTMLDivElement>(null)
    const isScrollingLeftRef = useRef(false)
    const isScrollingRightRef = useRef(false)
    const leftHeaderRef = useRef<HTMLDivElement | null>(null);
    const rightHeaderRef = useRef<HTMLDivElement | null>(null);
    const itmHeaderRef = useRef<HTMLDivElement | null>(null);
    const itmBodyRef = useRef<HTMLDivElement | null>(null);
    const isMirrorScroll = useSelector(selectOptionChainMirrorScroll(optionChainViewKey, showSingleExpiry ? 'extended' : 'normal'))
    const expandedRows = useSelector(selectExpandedRows(optionChainViewKey))
    const requiredOptionExpiries = useMemo(() => {
        const result = (optionExpiries ?? []).filter((o) => !o.derived).filter((o, i) => showSingleExpiry ? expandedRows?.includes(i?.toString()) : true)
        return showSingleExpiry ? (result[0] ? [result[0]] : []) : result
    }, [optionExpiries, expandedRows, showSingleExpiry])
    const selectedColumns = useSelector(selectOptionChainColumns(optionChainViewKey, showSingleExpiry ? 'extended' : 'normal'))
    const { width, ref } = useResizeDetector()
    const [noOfColumnsVisible, setNoOfColumnsVisible] = useState(0)
    const [noOfColumnsVisibleInitalized, setNoOfColumnsVisibleInitalized] = useState(false)
    const quantityInLots: boolean = useSelector(selectShowOptionChainQuantityInLots)
    const timeInterval = useSelector(selectOptionChainTimeInterval(optionChainViewKey, showSingleExpiry ? 'extended' : 'normal'))
    const noOfVisibleStrikes = useSelector(selectOptionChainVisibleStrikes)
    const optionchainData = useSelector(selectUnderlyingOptionsTradeData(token))
    const reduxTableView = useSelector(selectOptionChainTableView);
    const selectedOptionChainTableView = useMemo(
        () =>
            showSingleExpiry
                ? (selectedOptionChainTableViewProp ?? reduxTableView)
                : reduxTableView,
        [showSingleExpiry, selectedOptionChainTableViewProp, reduxTableView]
    );
    const renderedFirstTimeRef = useRef(false);
    const prevExpandedRows = useRef<string[]>([])

    useLayoutEffect(()=>{
        if(isMirrorScroll) {
            if(prevExpandedRows.current.length===0 && expandedRows.length > 0) {
                requestAnimationFrame(() => {
                    const left = leftScrollRef.current?.firstChild as HTMLDivElement
                    const right = rightScrollRef.current?.firstChild as HTMLDivElement
                    const leftHeader = leftHeaderRef.current?.firstChild as HTMLDivElement
                    const rightHeader = rightHeaderRef.current?.firstChild as HTMLDivElement
                    if (!left || !right || !leftHeader || !rightHeader) return
                    if(isMirrorScroll) {
                        left.scrollLeft = leftHeader.scrollLeft
                        right.scrollLeft = rightHeader.scrollLeft
                    }
                })
            }
            prevExpandedRows.current = expandedRows
        }
    }, [expandedRows, isMirrorScroll])

    const setPosFun = useCallback((scroolToExisting = false) => {
            const left = leftScrollRef.current?.firstChild as HTMLDivElement
            const right = rightScrollRef.current?.firstChild as HTMLDivElement
            const leftHeader = leftHeaderRef.current?.firstChild as HTMLDivElement
            const rightHeader = rightHeaderRef.current?.firstChild as HTMLDivElement
            if (!left || !right || !leftHeader || !rightHeader) return
            const scrollThreshold = scroolToExisting ? right.scrollLeft : 0
            left.scrollLeft = left.scrollWidth - left.clientWidth
            right.scrollLeft = scrollThreshold
            if (leftHeader) {
                leftHeader.scrollLeft = leftHeader.scrollWidth - leftHeader.clientWidth
            }
            if (rightHeader) {
                rightHeader.scrollLeft = scrollThreshold
            }
            if (!renderedFirstTimeRef.current) {
                renderedFirstTimeRef.current = true
            }
    }, [])

    useLayoutEffect(() => {
        if (!isMirrorScroll) return
        if (reduxTableView === OptionChainTableView.NORMAL) {
            if (!renderedFirstTimeRef.current) {
                setPosFun()
            } else {
                const timeout = setTimeout(setPosFun, 0)
                return () => clearTimeout(timeout)
            }
        }
    }, [isMirrorScroll, setPosFun, reduxTableView, token, selectedColumns])

    useLayoutEffect(() => {
        if (!isMirrorScroll) return
        if (reduxTableView === OptionChainTableView.NORMAL && noOfColumnsVisibleInitalized) {
            const setInitialScroll = () => {
                const left = leftScrollRef.current?.firstChild as HTMLDivElement
                const leftHeader = leftHeaderRef.current?.firstChild as HTMLDivElement
                if (left && leftHeader && left.scrollWidth > left.clientWidth) {
                    left.scrollLeft = left.scrollWidth - left.clientWidth
                    leftHeader.scrollLeft = leftHeader.scrollWidth - leftHeader.clientWidth
                }
            }
            
            setInitialScroll()
            
            const timeout1 = setTimeout(setInitialScroll, 0)
            const timeout2 = setTimeout(setInitialScroll, 100)
            
            return () => {
                clearTimeout(timeout1)
                clearTimeout(timeout2)
            }
        }
    }, [isMirrorScroll, reduxTableView, noOfColumnsVisibleInitalized])

    const widthChangeListener = useCallback(
        debounce(300, (width) => {
            let value: number = 0
            if (showSingleExpiry) {
                setNoOfColumnsVisible(selectedColumns?.length ?? 0)
                if (!noOfColumnsVisibleInitalized) setNoOfColumnsVisibleInitalized(true)
                return
            }
            if (width) {
                if (width >= 1400) value = 5
                else if (width >= 1200) value = 4
                else if (width >= 1000) value = 3
                else if (width >= 700) value = 2
                else value = 1
                if (selectedOptionChainTableView !== OptionChainTableView.NORMAL) {
                    value = Math.min(value * 2, 5)
                }
                setNoOfColumnsVisible(Math.min(value, selectedColumns?.length ?? 0))
                if (!noOfColumnsVisibleInitalized) setNoOfColumnsVisibleInitalized(true)
            }
        }),
        [selectedColumns, selectedOptionChainTableView, showSingleExpiry]
    )
    const tableMinWidth = useMemo(() => {
        const columnsCount = showSingleExpiry ? selectedColumns?.length ?? 0 : noOfColumnsVisible

        return Math.max(columnsCount * 100, 500)
    }, [noOfColumnsVisible, selectedColumns, showSingleExpiry])

    useEffect(() => {
        widthChangeListener(width)

        if (width && isMirrorScroll && reduxTableView === OptionChainTableView.NORMAL) {
            setTimeout(() => {
                const left = leftScrollRef.current?.firstChild as HTMLDivElement
                const leftHeader = leftHeaderRef.current?.firstChild as HTMLDivElement
                if (left && leftHeader && left.scrollWidth > left.clientWidth) {
                    left.scrollLeft = left.scrollWidth - left.clientWidth
                    leftHeader.scrollLeft = leftHeader.scrollWidth - leftHeader.clientWidth
                }
            }, 50)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [width, selectedColumns, isMirrorScroll, reduxTableView])

    useEffect(() => {
        let currentHoveredStrike: string | null = null
        let currentHoveredExpiry: string | null = null
        const attachedRows = new WeakSet<Element>()

        const handleMouseEnter = (e: Event) => {
            const row = e.currentTarget as HTMLElement
            if (!row) return

            const classList = Array.from(row.classList)
            const strikeClass = classList.find(c => c.startsWith('option-chain-row-'))
            const expiryClass = classList.find(c => c.startsWith('expiry-'))
            if (!strikeClass || !expiryClass) return
            if (currentHoveredStrike === strikeClass && currentHoveredExpiry === expiryClass) return
            currentHoveredStrike = strikeClass
            currentHoveredExpiry = expiryClass
            const allMatchingRows = document.querySelectorAll(`.${strikeClass}.${expiryClass}`)
            allMatchingRows.forEach(el => el.classList.add('synced-hover'))

            const table = row.closest('.option-chain-table')
            if (table) table.classList.add('table-hover-active')
        }

        const handleMouseLeave = (e: Event) => {
            const row = e.currentTarget as HTMLElement
            if (!row) return

            const classList = Array.from(row.classList)
            const strikeClass = classList.find(c => c.startsWith('option-chain-row-'))
            const expiryClass = classList.find(c => c.startsWith('expiry-'))
            if (!strikeClass || !expiryClass) return

            currentHoveredStrike = null
            currentHoveredExpiry = null

            const allMatchingRows = document.querySelectorAll(`.${strikeClass}.${expiryClass}`)
            allMatchingRows.forEach(el => el.classList.remove('synced-hover'))

            const table = row.closest('.option-chain-table')
            if (table) table.classList.remove('table-hover-active')
        }

        const attachListeners = () => {
            const rows = document.querySelectorAll('.option-chain-table tbody tr.option-chain-row, .option-chain-table tbody tr.atm-strike')
            rows.forEach(row => {
                if (!attachedRows.has(row)) {
                    row.addEventListener('mouseenter', handleMouseEnter)
                    row.addEventListener('mouseleave', handleMouseLeave)
                    attachedRows.add(row)
                }
            })
        }

        const detachListeners = () => {
            const rows = document.querySelectorAll('.option-chain-table tbody tr.option-chain-row, .option-chain-table tbody tr.atm-strike')
            rows.forEach(row => {
                row.removeEventListener('mouseenter', handleMouseEnter)
                row.removeEventListener('mouseleave', handleMouseLeave)
            })
        }
        const timeoutId1 = setTimeout(attachListeners, 100)
        const timeoutId2 = setTimeout(attachListeners, 300)
        const timeoutId3 = setTimeout(attachListeners, 600)

        const observer = new MutationObserver(() => {
            attachListeners()
        })

        const observerTimeout = setTimeout(() => {
            const container = document.getElementById(OPTION_CHAIN_TABLE_ID)
            if (container) {
                observer.observe(container, {
                    childList: true,
                    subtree: true
                })
            }
        }, 100)

        return () => {
            clearTimeout(timeoutId1)
            clearTimeout(timeoutId2)
            clearTimeout(timeoutId3)
            clearTimeout(observerTimeout)
            observer.disconnect()
            detachListeners()
        }
    }, [requiredOptionExpiries, expandedRows, selectedOptionChainTableView])

    useEffect(() => {
        optionExpiriesDataRef.current = optionExpiriesData
    }, [optionExpiriesData])


    useEffect(() => {
        const left = leftScrollRef.current?.firstChild as HTMLDivElement
        const right = rightScrollRef.current?.firstChild as HTMLDivElement
        const leftHeader = leftHeaderRef.current?.firstChild as HTMLDivElement
        const rightHeader = rightHeaderRef.current?.firstChild as HTMLDivElement
        if (!left || !right) return
        
        let leftScrollTimeout: number | null = null
        let rightScrollTimeout: number | null = null
        
        const handleLeftScroll = () => {
            if (isScrollingLeftRef.current) return
            
            isScrollingRightRef.current = true
            
            if (rightScrollTimeout) {
                clearTimeout(rightScrollTimeout)
            }
            
            right.scrollTop = left.scrollTop
            if (leftHeader) {
                if (expandedRows.length > 0) leftHeader.scrollLeft = left.scrollLeft
            } 
            if (isMirrorScroll) {
                const leftScrollPercentage = left.scrollLeft / (left.scrollWidth - left.clientWidth)
                const rightMaxScroll = right.scrollWidth - right.clientWidth
                const newRightScroll = rightMaxScroll * (1 - leftScrollPercentage)
                right.scrollLeft = newRightScroll
                if (rightHeader && expandedRows.length > 0) {
                    rightHeader.scrollLeft = newRightScroll
                }
            } else {
                right.scrollLeft = left.scrollLeft
                if (rightHeader && expandedRows.length > 0) {
                    rightHeader.scrollLeft = left.scrollLeft
                }
            }
            
            rightScrollTimeout = window.setTimeout(() => {
                isScrollingRightRef.current = false
                rightScrollTimeout = null
            }, 50)
        }
        
        const handleRightScroll = () => {
            if (isScrollingRightRef.current) return
            
            isScrollingLeftRef.current = true
            
            if (leftScrollTimeout) {
                clearTimeout(leftScrollTimeout)
            }
            
            left.scrollTop = right.scrollTop
            if (rightHeader) {
                if (expandedRows.length > 0) rightHeader.scrollLeft = right.scrollLeft
            }
            if (isMirrorScroll) {
                const rightScrollPercentage = right.scrollLeft / (right.scrollWidth - right.clientWidth)
                const leftMaxScroll = left.scrollWidth - left.clientWidth
                const newLeftScroll = leftMaxScroll * (1 - rightScrollPercentage)
                left.scrollLeft = newLeftScroll
                if (leftHeader && expandedRows.length > 0) {
                    leftHeader.scrollLeft = newLeftScroll
                }
            } else {
                left.scrollLeft = right.scrollLeft
                if (leftHeader && expandedRows.length > 0) {
                    leftHeader.scrollLeft = right.scrollLeft
                }
            }

            leftScrollTimeout = window.setTimeout(() => {
                isScrollingLeftRef.current = false
                leftScrollTimeout = null
            }, 50)
        }
        
        left.addEventListener('scroll', handleLeftScroll, { passive: true })
        right.addEventListener('scroll', handleRightScroll, { passive: true })

        return () => {
            left.removeEventListener('scroll', handleLeftScroll)
            right.removeEventListener('scroll', handleRightScroll)
            if (leftScrollTimeout) clearTimeout(leftScrollTimeout)
            if (rightScrollTimeout) clearTimeout(rightScrollTimeout)
        }
    }, [requiredOptionExpiries, noOfColumnsVisibleInitalized, isMirrorScroll, selectedOptionChainTableView, expandedRows])

    // Properly synchronize ITM header/body scroll both ways
    useEffect(() => {
        const header = itmHeaderRef?.current as HTMLDivElement;
        const body = itmBodyRef?.current as HTMLDivElement;
        if (!header || !body) return;
        const headerScrollable = header.firstChild as HTMLDivElement;
        const bodyScrollable = body.firstChild as HTMLDivElement;
        if (!headerScrollable || !bodyScrollable) return;

        // Add will-change for better Safari performance
        headerScrollable.style.willChange = 'scroll-position'
        bodyScrollable.style.willChange = 'scroll-position'

        const handleBodyScroll = () => {
            requestAnimationFrame(() => {
                if (headerScrollable.scrollLeft !== bodyScrollable.scrollLeft) {
                    headerScrollable.scrollLeft = bodyScrollable.scrollLeft;
                }
            })
        }

        bodyScrollable.addEventListener("scroll", handleBodyScroll, { passive: true });

        return () => {
            bodyScrollable.removeEventListener("scroll", handleBodyScroll);
            headerScrollable.style.willChange = 'auto'
            bodyScrollable.style.willChange = 'auto'
        };
    }, [requiredOptionExpiries, noOfColumnsVisibleInitalized, selectedOptionChainTableView]);

    const bidAskColumnWidth = useMemo(() => {
        if (showSingleExpiry) return 0
        if (noOfColumnsVisible === 0) return 230
        if (noOfColumnsVisible === 1) return 120
        if (noOfColumnsVisible === 2) return 80
        return 60
    }, [noOfColumnsVisible, showSingleExpiry])

    const strikeColWidth = useMemo(() => {
        if (showSingleExpiry) return 100
        return bidAskColumnWidth > 80 ? bidAskColumnWidth : 100
    }, [bidAskColumnWidth, showSingleExpiry])
    const toggleExpandedRow = useCallback(
        (index: number) => {
            if (disableRowCollapse) {
                return;
            }
            dispatch(
                toggleOptionChainExpandedRow({
                    key: index.toString(),
                    expirationDate: optionExpiries?.[index].expirationDate ?? '',
                    optionChainViewKey: optionChainViewKey
                })
            )
            if (!isOptionChainLiveView(optionChainViewKey)) return
            if (
                primary &&
                optionExpiries &&
                optionchainData &&
                !expandedRows.includes(index.toString()) &&
                (optionchainData[index].trades.length === 0 || JSON.stringify(timeInterval) !== JSON.stringify(optionchainData[index].timebucket))
            ) {
                const expandedRowsPassed = [...expandedRows, index.toString()]
                expandedRowsPassed.sort((a, b) => Number(a) - Number(b))
                const payload: GetOptionChainRequestV2 = {
                    security: {
                        code: primary.code,
                        exchange: primary.exchange
                    },
                    expiries: [optionExpiries[index].expiry],
                    noOfStrikes: noOfVisibleStrikes === 'all' ? -1 : Number(noOfVisibleStrikes),
                    requestedFields: [],
                    timeBucket: timeInterval,
                    sendFutures: true
                }
                fetchOptionChainV2(payload, dispatch, optionchainData, 'both', true, expandedRowsPassed)
            }
        },
        [dispatch, expandedRows, noOfVisibleStrikes, optionChainViewKey, optionExpiries, optionchainData, primary, timeInterval, disableRowCollapse]
    )
    const expiryColumns: CustomColumnDef<OptionExpiryType>[] = useMemo(
        () => selectedOptionChainTableView === OptionChainTableView.NORMAL ? [
            {
                header: 'Expiration Date',
                field: 'expiration',
                align: 'left',
                sx: { padding: '0 !important', cursor: 'pointer', height: '31px' },
                cellRenderer: (row, index) => <ExpiryColumnRender RecKey={index.toString()} text={JSON.stringify(row)} optionTypeOnly={showSingleExpiry} />
            },
            {
                header: 'Days',
                field: 'numberOfDaysToExpiry',
                align: 'center',
                sx: { padding: '0 !important', width: 'auto', cursor: 'pointer', height: '31px', minWidth: strikeColWidth },
                cellRenderer: (row) => (
                    <div className={cx(classes.strikeColumn, classes.hFull, classes.p1, 'font-feature-num')}>
                        {row.numberOfDaysToExpiry === 0 ? 'Today' : `${row.numberOfDaysToExpiry}d`}
                    </div>
                )
            },
            {
                header: 'IVx',
                field: 'ivxExpiry',
                align: 'right',
                sx: { padding: '0 !important', cursor: 'pointer', height: '31px' },
                cellRenderer: (row, index) => <ExpiryPcrRender RecKey={index.toString()} ivxExpiry={row.ivxExpiry} pcrExpiry={row.pcrExpiry} optionTypeOnly={showSingleExpiry} />
            }
        ] : [
            {
                header: 'Expiration Date',
                field: 'expiration',
                align: 'left',
                sx: { padding: 0, cursor: 'pointer', height: '31px' },
                cellRenderer: (row, index) => <ExpiryRowRenderOnly RecKey={index.toString()} text={JSON.stringify(row)} ivxExpiry={row.ivxExpiry} pcrExpiry={row.pcrExpiry} />
            },
        ],
        [classes, cx, strikeColWidth, selectedOptionChainTableView, showSingleExpiry]
    )

    const filteredColumns = expiryColumns.filter((col) =>
        selectedColumns.includes(col.field)
    );
    const totalColumnWidthUnits = useMemo(() => {
        let total = 0
        selectedColumns.forEach((column: string) => {
            column.split('|').forEach((subColumn) => {
                total += colsWidth(showSingleExpiry)[subColumn] ?? 1
            })
        })
        return total > 0 ? total : 1
    }, [selectedColumns, showSingleExpiry])

    const strikeColumns = useCallback(
        (topLevelKey: string): CustomColumnDef<optionTradeDataType>[] => {
            if (selectedOptionChainTableView === OptionChainTableView.CALLS) {
                return [
                    {
                        header: (
                            <Typography sx={{ padding: '0px', fontSize: '14px', textAlign: 'center' }}>
                                Strikes
                            </Typography>
                        ),
                        field: 'callOption',
                        align: 'center',
                        sx: {
                            padding: 0,
                            whiteSpace: 'nowrap',
                            position: 'sticky',
                            left: 0,
                            top: 0,
                            zIndex: 1000,
                            width: "120px",
                            minWidth: "100px",
                            maxWidth: "120px !important"
                        },

                        tableCellClassName: 'sticky-col'
                    } as CustomColumnDef<optionTradeDataType>,

                    ...((noOfColumnsVisible > 0
                        ? [
                            ...selectedColumns.map((v: string) => {
                                const colSpan = spanCols.includes(
                                    optionColumnOptions.find((c) => c.value === v)?.value ?? ''
                                )
                                    ? 2
                                    : 1;

                                const columns: CustomColumnDef<optionTradeDataType>[] = [];
                                for (let s = 0; s < colSpan; s++) {
                                    const subColumn = optionColumnOptions
                                        .find((c) => c.value === v)
                                        ?.value?.split('|')?.[s];
                                    const columnWidthUnit =
                                        colsWidth(showSingleExpiry)[subColumn ?? ''] ?? 1;

                                    const column: CustomColumnDef<optionTradeDataType> = {
                                        header:
                                            s === 0 ? (
                                                <Typography sx={{ padding: '0px', fontSize: '14px' }}>
                                                    {optionColumnOptions.find((c) => c.value === v)?.label}
                                                    {(v === 'openInterest' || v === 'volume') &&
                                                        quantityInLots
                                                        ? ' (lots)'
                                                        : null}
                                                </Typography>
                                            ) : null,
                                        field: 'callOption',
                                        sx: {
                                            padding: 0,
                                            width: 'auto',
                                            minWidth: `${colMinWidth[subColumn ?? 'outlook'] ?? 125
                                                }px`,
                                        },
                                        colSpan: s === 0 ? colSpan : 0,
                                    };
                                    columns.push(column);
                                }
                                return columns;
                            }),
                        ]
                        : []
                    ).flat() as CustomColumnDef<optionTradeDataType>[]),
                ];
            }

            if (selectedOptionChainTableView === OptionChainTableView.PUTS) {
                return [
                    {
                        header: (
                            <Typography sx={{ padding: '0px', fontSize: '14px', textAlign: 'center' }}>
                                Strikes
                            </Typography>
                        ),
                        field: 'putOption',
                        align: 'center',
                        sx: {
                            padding: 0,
                            whiteSpace: 'nowrap',
                            position: 'sticky',
                            left: 0,
                            zIndex: 1000,
                            width: "120px",
                            minWidth: "100px",
                            maxWidth: "120px !important"

                        },
                        tableCellClassName: 'sticky-col'
                    } as CustomColumnDef<optionTradeDataType>,

                    ...((noOfColumnsVisible > 0
                        ? [
                            ...[...selectedColumns].map((v: string) => {

                                const colSpan = spanCols.includes(
                                    optionColumnOptions.find((c) => c.value === v)?.value ?? ''
                                )
                                    ? 2
                                    : 1;

                                const columns: CustomColumnDef<optionTradeDataType>[] = [];
                                for (let s = 0; s < colSpan; s++) {
                                    const subColumn = optionColumnOptions
                                        .find((c) => c.value === v)
                                        ?.value?.split('|')?.[s];
                                    const columnWidthUnit =
                                        colsWidth(showSingleExpiry)[subColumn ?? ''] ?? 1;

                                    const column: CustomColumnDef<optionTradeDataType> = {
                                        header:
                                            s === 0 ? (
                                                <Typography sx={{ padding: '0px', fontSize: '14px' }}>
                                                    {optionColumnOptions.find((c) => c.value === v)?.label}
                                                    {(v === 'openInterest' || v === 'volume') &&
                                                        quantityInLots
                                                        ? ' (lots)'
                                                        : null}
                                                </Typography>
                                            ) : null,
                                        field: 'putOption',
                                        sx: {
                                            padding: 0,
                                            width: 'auto',
                                            minWidth: `${colMinWidth[subColumn ?? 'outlook'] ?? 125
                                                }px`,
                                        },
                                        colSpan: s === 0 ? colSpan : 0,
                                    };
                                    columns.push(column);
                                }
                                return columns;
                            }),
                        ]
                        : []
                    ).flat() as CustomColumnDef<optionTradeDataType>[]),
                ];
            }
            if (selectedOptionChainTableView === OptionChainTableView.ITM) {
                return [
                    {
                        header: (
                            <Typography sx={{ padding: '0px', fontSize: '14px', textAlign: 'center' }}>
                                Strikes
                            </Typography>
                        ),
                        field: 'itmOption',
                        align: 'center',
                        sx: {
                            padding: 0,
                            whiteSpace: 'nowrap',
                            position: 'sticky',
                            left: 0,
                            zIndex: 1000,
                            width: "120px",
                            minWidth: "100px",
                            maxWidth: "120px !important"
                        },
                        tableCellClassName: 'sticky-col'
                    } as CustomColumnDef<optionTradeDataType>,

                    ...((noOfColumnsVisible > 0
                        ? [
                            ...selectedColumns.map((v: string) => {
                                const colSpan = spanCols.includes(
                                    optionColumnOptions.find((c) => c.value === v)?.value ?? ''
                                )
                                    ? 2
                                    : 1;

                                const columns: CustomColumnDef<optionTradeDataType>[] = [];
                                for (let s = 0; s < colSpan; s++) {
                                    const subColumn = optionColumnOptions
                                        .find((c) => c.value === v)
                                        ?.value?.split('|')?.[s];
                                    const columnWidthUnit =
                                        colsWidth(showSingleExpiry)[subColumn ?? ''] ?? 1;

                                    const column: CustomColumnDef<optionTradeDataType> = {
                                        header:
                                            s === 0 ? (
                                                <Typography sx={{ padding: '0px', fontSize: '14px' }}>
                                                    {optionColumnOptions.find((c) => c.value === v)?.label}
                                                    {(v === 'openInterest' || v === 'volume') &&
                                                        quantityInLots
                                                        ? ' (lots)'
                                                        : null}
                                                </Typography>
                                            ) : null,
                                        field: 'itmOption',
                                        sx: {
                                            padding: 0,
                                            width: 'auto',
                                            minWidth: `${colMinWidth[subColumn ?? 'outlook'] ?? 125
                                                }px`,
                                        },
                                        colSpan: s === 0 ? colSpan : 0,
                                    };
                                    columns.push(column);
                                }
                                return columns;
                            }),
                        ]
                        : []
                    ).flat() as CustomColumnDef<optionTradeDataType>[]),
                ];
            }
            if (selectedOptionChainTableView === OptionChainTableView.OTM) {
                return [
                    {
                        header: (
                            <Typography sx={{ padding: '0px', fontSize: '14px', textAlign: 'center' }}>
                                Strikes
                            </Typography>
                        ),
                        field: 'otmOption',
                        align: 'center',
                        sx: {
                            padding: 0,
                            whiteSpace: 'nowrap',
                            position: 'sticky',
                            left: 0,
                            zIndex: 1000,
                            width: "120px",
                            minWidth: "100px",
                            maxWidth: "120px !important"
                        },
                        tableCellClassName: 'sticky-col'
                    } as CustomColumnDef<optionTradeDataType>,

                    ...((noOfColumnsVisible > 0
                        ? [
                            ...selectedColumns.map((v: string) => {
                                const colSpan = spanCols.includes(
                                    optionColumnOptions.find((c) => c.value === v)?.value ?? ''
                                )
                                    ? 2
                                    : 1;

                                const columns: CustomColumnDef<optionTradeDataType>[] = [];
                                for (let s = 0; s < colSpan; s++) {
                                    const subColumn = optionColumnOptions
                                        .find((c) => c.value === v)
                                        ?.value?.split('|')?.[s];
                                    const columnWidthUnit =
                                        colsWidth(showSingleExpiry)[subColumn ?? ''] ?? 1;

                                    const column: CustomColumnDef<optionTradeDataType> = {
                                        header:
                                            s === 0 ? (
                                                <Typography sx={{ padding: '0px', fontSize: '14px' }}>
                                                    {optionColumnOptions.find((c) => c.value === v)?.label}
                                                    {(v === 'openInterest' || v === 'volume') &&
                                                        quantityInLots
                                                        ? ' (lots)'
                                                        : null}
                                                </Typography>
                                            ) : null,
                                        field: 'otmOption',
                                        sx: {
                                            padding: 0,
                                            width: 'auto',
                                            minWidth: `${colMinWidth[subColumn ?? 'outlook'] ?? 125
                                                }px`,
                                        },
                                        colSpan: s === 0 ? colSpan : 0,
                                    };
                                    columns.push(column);
                                }
                                return columns;
                            }),
                        ]
                        : []
                    ).flat() as CustomColumnDef<optionTradeDataType>[]),
                ];
            }

            if (selectedOptionChainTableView === OptionChainTableView.NORMAL) {
                return [
                    ...((noOfColumnsVisible > 0
                        ? [
                            ...[...selectedColumns].map((v: string, i: number, arr: string[]) => {
                                const currentindex = isMirrorScroll
                                    ? arr.length - i - 1
                                    : i;
                                const val = isMirrorScroll ? arr[currentindex] : v;
                                const colSpan = spanCols.includes(
                                    optionColumnOptions.find((c) => c.value === val)?.value ?? ''
                                )
                                    ? 2
                                    : 1;
                                const columns: CustomColumnDef<optionTradeDataType>[] = [];
                                for (let s = 0; s < colSpan; s++) {
                                    const subColumn = optionColumnOptions
                                        .find((c) => c.value === val)
                                        ?.value?.split('|')?.[s];
                                    const columnWidthUnit =
                                        colsWidth(showSingleExpiry)[subColumn ?? ''] ?? 1;
                                    const column: CustomColumnDef<optionTradeDataType> = {
                                        header:
                                            s === 0 ? (
                                                <Typography sx={{ padding: '0px 4px !important', fontSize: '14px' }}>
                                                    {optionColumnOptions.find((c) => c.value === val)?.label}
                                                    {(val === 'openInterest' || val === 'volume') &&
                                                        quantityInLots
                                                        ? ' (lots)'
                                                        : null}
                                                </Typography>
                                            ) : null,
                                        field: 'callOption',
                                        sx: {
                                            padding: 0,
                                            width: 'auto',
                                            minWidth: `${colMinWidth[subColumn ?? 'outlook'] ?? 125}px`,
                                        },
                                        colSpan: s === 0 ? colSpan : 0,
                                    };
                                    columns.push(column);
                                }
                                return columns;
                            }),
                        ]
                        : []
                    ).flat() as CustomColumnDef<optionTradeDataType>[]),

                    {
                        header: (
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: 0.5,
                                    pointerEvents: 'auto',

                                }}
                            >
                                <Typography sx={{ padding: '0px', fontSize: '14px', textAlign: 'center' }}>
                                    Strikes
                                </Typography>
                                <Tooltip
                                    title={
                                        isMirrorScroll
                                            ? 'Switch to Simple Scroll'
                                            : 'Switch to Mirror Scroll'
                                    }
                                >
                                    <IconButton
                                        size="small"
                                        onClick={() => {
                                            commonActions.setOptionChainMirrorScroll(dispatch, {
                                                isMirrorScroll: !isMirrorScroll,
                                                optionChainViewKey: optionChainViewKey,
                                                type: showSingleExpiry ? 'extended' : 'normal'
                                            })
                                            const left = leftScrollRef.current?.firstChild as HTMLDivElement
                                            const right = rightScrollRef.current?.firstChild as HTMLDivElement
                                            const leftHeader = leftHeaderRef.current?.firstChild as HTMLDivElement
                                            const rightHeader = rightHeaderRef.current?.firstChild as HTMLDivElement
                                            if (!left || !right || !leftHeader || !rightHeader) return
                                            if (!isMirrorScroll) {

                                                left.scrollLeft = left.scrollWidth - left.clientWidth
                                                right.scrollLeft = 0
                                                if (leftHeader) {
                                                    leftHeader.scrollLeft = leftHeader.scrollWidth - leftHeader.clientWidth
                                                }
                                                if (rightHeader) {
                                                    rightHeader.scrollLeft = 0
                                                }
                                            } else {
                                                left.scrollLeft = 0
                                                right.scrollLeft = 0
                                                if (leftHeader) {
                                                    leftHeader.scrollLeft = 0
                                                }
                                                if (rightHeader) {
                                                    rightHeader.scrollLeft = 0
                                                }
                                            }
                                        }}
                                        sx={{ padding: '2px' }}
                                    >
                                        {isMirrorScroll ? (
                                            <MirrorIcon />
                                        ) : (
                                            <PauseIcon />
                                        )}
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        ),
                        field: 'strikeCol',
                        align: 'center',
                        sx: {
                            padding: 0,
                            width: 'auto',
                            cursor: 'pointer',
                            minWidth: strikeColWidth,
                        },
                    } as CustomColumnDef<optionTradeDataType>,

                    ...((noOfColumnsVisible > 0
                        ? [
                            ...[...selectedColumns].map(
                                (v: string, i: number, arr: string[]) => {
                                    const val = v;
                                    const colSpan = spanCols.includes(
                                        optionColumnOptions.find((c) => c.value === val)?.value ?? ''
                                    )
                                        ? 2
                                        : 1;
                                    const columns: CustomColumnDef<optionTradeDataType>[] = [];
                                    for (let s = 0; s < colSpan; s++) {

                                        const subColumn = optionColumnOptions
                                            .find((c) => c.value === val)
                                            ?.value?.split('|')?.[s];
                                        const column: CustomColumnDef<optionTradeDataType> = {
                                            header:
                                                s === 0 ? (
                                                    <Typography
                                                        sx={{ padding: '0px 4px !important', fontSize: '14px' }}
                                                    >
                                                        {
                                                            optionColumnOptions.find(
                                                                (c) => c.value === val
                                                            )?.label
                                                        }
                                                        {(val === 'openInterest' || val === 'volume') &&
                                                            quantityInLots
                                                            ? ' (lots)'
                                                            : null}
                                                    </Typography>
                                                ) : null,
                                            field: 'putOption',
                                            sx: {
                                                padding: 0,
                                                width: 'auto',
                                                minWidth: `${colMinWidth[subColumn ?? 'outlook'] ?? 125
                                                    }px`,
                                            },
                                            colSpan: s === 0 ? colSpan : 0,
                                        };
                                        columns.push(column);
                                    }
                                    return columns;
                                }
                            ),
                        ]
                        : []
                    ).flat() as CustomColumnDef<optionTradeDataType>[]),
                ];
            } return [];
        },
        [
            selectedOptionChainTableView,
            noOfColumnsVisible,
            selectedColumns,
            strikeColWidth,
            quantityInLots,
            showSingleExpiry,
            isMirrorScroll,
        ]
    );

    const collapseComponentProps: Partial<OptionChainExpandedTableProps> = useMemo(
        () => ({
            strikeColumns: strikeColumns,
            noOfColumnsVisible: noOfColumnsVisible,
            bidAskColumnWidth: bidAskColumnWidth,
            strikeColWidth: strikeColWidth,
            optionChainViewKey: optionChainViewKey,
            showSingleExpiry: showSingleExpiry,
            isMirrorScroll: isMirrorScroll,
            underlyingToken: underlyingTokenOverride,
        }),
        [strikeColumns, noOfColumnsVisible, bidAskColumnWidth, strikeColWidth, optionChainViewKey, showSingleExpiry, isMirrorScroll, underlyingTokenOverride]
    )

    const collapsePropsCallOption = useMemo(
        () => ({ ...collapseComponentProps, type: 'callOption' as const }),
        [collapseComponentProps]
    )
    const collapsePropsStrikeCol = useMemo(
        () => ({ ...collapseComponentProps, type: 'strikeCol' as const }),
        [collapseComponentProps]
    )
    const collapsePropsPutOption = useMemo(
        () => ({ ...collapseComponentProps, type: 'putOption' as const }),
        [collapseComponentProps]
    )
    const collapsePropsOtmOption = useMemo(
        () => ({ ...collapseComponentProps, type: 'otmOption' as const }),
        [collapseComponentProps]
    )
    const collapsePropsItmOption = useMemo(
        () => ({ ...collapseComponentProps, type: 'itmOption' as const }),
        [collapseComponentProps]
    )

    const renderEmptyData = useCallback(
        (loading: boolean, colSpan: number) => {
            return (
                <tr>
                    <td colSpan={colSpan} className={classes.emptyDataLoader}>
                        {loading ? <Loading size={30} /> : <p className={classes.textAlignCenter}>No Data Found</p>}
                    </td>
                </tr>
            )
        },
        [classes]
    )

    const hasOptionChainData = !!optionchainData?.length
    const normalViewNotReady =
        selectedOptionChainTableView === OptionChainTableView.NORMAL &&
        !showSingleExpiry &&
        (!width || !noOfColumnsVisibleInitalized)
    const isTableReady = showSingleExpiry
        ? noOfColumnsVisibleInitalized
        : !!width && noOfColumnsVisibleInitalized
    const showLoadingShimmer = !hasOptionChainData || normalViewNotReady

    return (
        <OptionChainPopoverProvider>
        <Paper ref={ref} className={cx(classes.flex, classes.flexCol, classes.hFull, classes.simpleBarScroller)} id={OPTION_CHAIN_TABLE_ID} sx={{ gap: '2px !important', borderRadius: 0 }}>
            {showLoadingShimmer ? (
                <SubtleShimmer loading fill>
                    <OptionChainSkeleton />
                </SubtleShimmer>
            ) : null}
            {!showLoadingShimmer && isTableReady && selectedOptionChainTableView == OptionChainTableView.NORMAL ? (
                <>
                    <Paper className={`${classes.flex} ${classes.flexCol} ${classes.hFull}`} sx={{ borderRadius: 0, width: '100%', height: '100%' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'row', height: 'auto', width: '100%' }}>
                            <Box sx={{ flex: '0 0 calc((100% - 100px) / 2)', display: 'flex', flexDirection: 'column', maxWidth: 'calc((100% - 100px) / 2)' }}>
                                <Box
                                    sx={{
                                        position: 'sticky',
                                        top: 0,
                                        zIndex: 99
                                    }}
                                >
                                    <Box
                                        ref={leftHeaderRef}
                                        sx={{
                                            overflow: 'hidden',
                                            '&::-webkit-scrollbar': { display: 'none' },
                                            scrollbarWidth: 'none',
                                            msOverflowStyle: 'none',
                                            pointerEvents: 'none'
                                        }}
                                    >
                                        <CustomTable
                                            columns={strikeColumns('').filter((col) => col.field === 'callOption')}
                                            rows={[]}
                                            keyFieldInData="key"
                                            className={classes.emptyHeader}
                                            disableColGroup
                                            tableContainerSxProps={{
                                                overflowX: 'auto',
                                                scrollbarWidth: 'none',
                                                msOverflowStyle: 'none',
                                                width: 'auto',
                                                '& th': {
                                                    whiteSpace: 'nowrap',
                                                    height: '30px !important',
                                                    padding: '0px !important'
                                                }
                                            }}
                                            tableSxProps={{
                                                tableLayout: 'auto !important'
                                            }}
                                        />
                                    </Box>
                                </Box>
                                <Box
                                    ref={leftScrollRef}
                                    sx={{
                                        flex: 1,
                                        '&::-webkit-scrollbar': { display: 'none' },
                                        overflowX: 'hidden',
                                        overflowY: 'hidden',
                                        scrollbarWidth: 'none',
                                        msOverflowStyle: 'none'
                                    }}
                                >
                                    <CustomTable
                                        hideHeader
                                        columns={expiryColumns.filter((col) => col.field === 'expiration')}
                                        rows={requiredOptionExpiries ?? []}
                                        RenderCollapsibleContent={OptionChainExpandedTable}
                                        collapseComponentProps={collapsePropsCallOption}
                                        expandedRows={disableRowCollapse ? requiredOptionExpiries?.map((_, i) => i.toString()) : expandedRows}
                                        toggleExpandedRow={toggleExpandedRow}
                                        sortField="id"
                                        verticalDividers
                                        keyFieldInData="date"
                                        className="extended-option-chain"
                                        tableContainerSxProps={{
                                            overflowX: 'auto',
                                            overflowY: 'hidden',
                                            scrollbarWidth: 'none',
                                            msOverflowStyle: 'none',
                                            width: 'auto'
                                        }}
                                        tableSxProps={{
                                            tableLayout: 'auto !important'
                                        }}
                                    />
                                </Box>
                            </Box>
                            <Box
                                className="strike-column-header"
                                sx={(theme) => ({
                                    flex: '0 0 100px',
                                    display: 'flex',
                                    maxWidth: '100px',
                                    flexDirection: 'column',
                                    position: 'relative',
                                    borderLeft: `0.5px solid ${theme.palette.divider} !important`,
                                    borderRight: `0.5px solid ${theme.palette.divider} !important`
                                })}
                            >
                                <Box
                                    sx={{
                                        height: `30px`,
                                        overflow: 'hidden',
                                        padding: '0 !important',
                                        margin: '0 !important',
                                        position: 'sticky',
                                        top: 0,
                                        zIndex: 99,
                                        '&::-webkit-scrollbar': {
                                            display: 'none !important',
                                            width: '0 !important',
                                            height: '0 !important'
                                        },
                                        scrollbarWidth: 'none',
                                        msOverflowStyle: 'none',
                                        pointerEvents: 'none',
                                        '& table, & tbody, & thead, & tr, & th, & td, & th *, & td *': {
                                            height: '30px',
                                            lineHeight: '30px',
                                            margin: 0,
                                            padding: 0
                                        }
                                    }}
                                >
                                    <CustomTable
                                        columns={strikeColumns('').filter((col) => col.field === 'strikeCol')}
                                        rows={[]}
                                        keyFieldInData="key"
                                        className={classes.emptyHeader}
                                        disableColGroup
                                        tableSxProps={{
                                            tableLayout: 'auto !important'
                                        }}
                                        tableContainerSxProps={{
                                            overflow: 'hidden',
                                            scrollbarWidth: 'none',
                                            msOverflowStyle: 'none',
                                            width: 'auto'
                                        }}
                                    />
                                </Box>
                                <Box sx={{ flex: 1, overflowY: 'hidden' }}>
                                    <CustomTable
                                        hideHeader
                                        columns={expiryColumns.filter((col) => col.field === 'numberOfDaysToExpiry')}
                                        rows={requiredOptionExpiries ?? []}
                                        RenderCollapsibleContent={OptionChainExpandedTable}
                                        collapseComponentProps={collapsePropsStrikeCol}
                                        expandedRows={disableRowCollapse ? requiredOptionExpiries?.map((_, i) => i.toString()) : expandedRows}
                                        toggleExpandedRow={toggleExpandedRow}
                                        sortField="id"
                                        keyFieldInData="date"
                                        tableContainerSxProps={{
                                            overflow: 'hidden',
                                            scrollbarWidth: 'none',
                                            msOverflowStyle: 'none',
                                            width: 'auto'
                                        }}
                                        tableSxProps={{
                                            tableLayout: 'auto !important'
                                        }}
                                    />
                                </Box>
                            </Box>
                            <Box sx={{ flex: '0 0 calc((100% - 100px) / 2)', display: 'flex', flexDirection: 'column', maxWidth: 'calc((100% - 100px) / 2)' }}>
                                <Box
                                    sx={{
                                        position: 'sticky',
                                        top: 0,
                                        zIndex: 99
                                    }}
                                >
                                    <Box
                                        ref={rightHeaderRef}
                                        sx={{
                                            overflowX: 'auto',
                                            overflowY: 'hidden',
                                            '&::-webkit-scrollbar': { display: 'none' },
                                            scrollbarWidth: 'none',
                                            msOverflowStyle: 'none',
                                            pointerEvents: 'none'
                                        }}
                                    >
                                        <CustomTable
                                            columns={strikeColumns('').filter((col) => col.field === 'putOption')}
                                            rows={[]}
                                            keyFieldInData="key"
                                            className={classes.emptyHeader}
                                            disableColGroup
                                            tableContainerSxProps={{
                                                overflowX: 'inherit',
                                                scrollbarWidth: 'none',
                                                msOverflowStyle: 'none',
                                                width: 'auto',
                                                '& th': {
                                                    whiteSpace: 'nowrap',
                                                    height: '30px !important'
                                                }
                                            }}
                                            tableSxProps={{
                                                tableLayout: 'auto !important'
                                            }}
                                        />
                                    </Box>
                                </Box>
                                <Box
                                    ref={rightScrollRef}
                                    sx={{
                                        flex: 1,
                                        '&::-webkit-scrollbar': { display: 'none' },
                                        overflowX: 'auto',
                                        overflowY: 'hidden',
                                        scrollbarWidth: 'none',
                                        msOverflowStyle: 'none',
                                        position: 'relative'
                                    }}
                                >
                                    <CustomTable
                                        hideHeader
                                        columns={expiryColumns.filter((col) => col.field === 'ivxExpiry')}
                                        rows={requiredOptionExpiries ?? []}
                                        RenderCollapsibleContent={OptionChainExpandedTable}
                                        collapseComponentProps={collapsePropsPutOption}
                                        expandedRows={disableRowCollapse ? requiredOptionExpiries?.map((_, i) => i.toString()) : expandedRows}
                                        toggleExpandedRow={toggleExpandedRow}
                                        sortField="id"
                                        verticalDividers
                                        keyFieldInData="date"
                                        className="extended-option-chain"
                                        tableContainerSxProps={{
                                            overflowX: 'auto',
                                            overflowY: 'hidden',
                                            scrollbarWidth: 'none',
                                            msOverflowStyle: 'none',
                                            width: 'auto'
                                        }}
                                        tableSxProps={{
                                            tableLayout: 'auto !important'
                                        }}
                                    />
                                </Box>
                            </Box>
                        </Box>
                    </Paper>
                </>
            ) : null}
            {!showLoadingShimmer && selectedOptionChainTableView === OptionChainTableView.CALLS && (
                <>
                    {' '}
                    <Paper className={`${classes.flex} ${classes.flexCol} ${classes.hFull}`} sx={{ borderRadius: 0, width: '100%', height: '100%' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'row', height: 'auto', width: '100%' }}>
                            <Box sx={{ flex: '0 0 calc((100%))', display: 'flex', flexDirection: 'column', maxWidth: 'calc((100%))' }}>
                                <Box
                                    sx={{
                                        position: 'sticky',
                                        top: 0,
                                        zIndex: 99
                                    }}
                                >
                                    <Box
                                        ref={itmHeaderRef}
                                        sx={{
                                            overflowX: 'auto',
                                            overflowY: 'hidden',
                                            '&::-webkit-scrollbar': { display: 'none' },
                                            scrollbarWidth: 'none',
                                            msOverflowStyle: 'none',
                                            pointerEvents: 'none'
                                        }}
                                    >
                                        <CustomTable
                                            columns={strikeColumns('').filter((col) => col.field === 'callOption')}
                                            rows={[]}
                                            keyFieldInData="key"
                                            className={classes.emptyHeader}
                                            disableColGroup
                                            tableContainerSxProps={{
                                                overflowX: 'auto',
                                                scrollbarWidth: 'none',
                                                msOverflowStyle: 'none',
                                                width: 'auto',
                                                '& th': {
                                                    whiteSpace: 'nowrap',
                                                    height: '30px !important',
                                                    padding: '0px !important'
                                                }
                                            }}
                                            tableSxProps={{
                                                tableLayout: 'auto !important'
                                            }}
                                        />
                                    </Box>
                                </Box>
                                <Box
                                    ref={itmBodyRef}
                                    sx={{
                                        flex: 1,
                                        '&::-webkit-scrollbar': { display: 'none' },
                                        overflowX: 'auto',
                                        overflowY: 'hidden',
                                        scrollbarWidth: 'none',
                                        msOverflowStyle: 'none',
                                        position: 'relative'
                                    }}
                                >
                                    <CustomTable
                                        hideHeader
                                        columns={expiryColumns.filter((col) => col.field === 'expiration')}
                                        rows={requiredOptionExpiries ?? []}
                                        RenderCollapsibleContent={OptionChainExpandedTable}
                                        collapseComponentProps={collapsePropsCallOption}
                                        expandedRows={disableRowCollapse ? requiredOptionExpiries?.map((_, i) => i.toString()) : expandedRows}
                                        toggleExpandedRow={toggleExpandedRow}
                                        sortField="id"
                                        verticalDividers
                                        keyFieldInData="date"
                                        className="extended-option-chain"
                                        tableContainerSxProps={{
                                            overflowX: 'auto',
                                            overflowY: 'hidden',
                                            scrollbarWidth: 'none',
                                            msOverflowStyle: 'none',
                                            width: 'auto'
                                        }}
                                        tableSxProps={{
                                            tableLayout: 'auto !important'
                                        }}
                                    />
                                </Box>
                            </Box>
                        </Box>
                    </Paper>
                </>
            )}
            {!showLoadingShimmer && selectedOptionChainTableView === OptionChainTableView.PUTS && (
                <>
                    {' '}
                    <Paper className={`${classes.flex} ${classes.flexCol} ${classes.hFull}`} sx={{ borderRadius: 0, width: '100%', height: '100%' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'row', height: 'auto', width: '100%' }}>
                            <Box sx={{ flex: '0 0 calc((100%))', display: 'flex', flexDirection: 'column', maxWidth: 'calc((100%))' }}>
                                <Box
                                    sx={{
                                        position: 'sticky',
                                        top: 0,
                                        zIndex: 99,
                                        backgroundColor: (theme) => (theme.palette.mode === 'dark' ? '#252525' : '#F5F5F5')
                                    }}
                                >
                                    <Box
                                        ref={itmHeaderRef}
                                        sx={{
                                            overflowX: 'auto',
                                            overflowY: 'hidden',
                                            '&::-webkit-scrollbar': { display: 'none' },
                                            scrollbarWidth: 'none',
                                            msOverflowStyle: 'none',
                                            pointerEvents: 'none'
                                        }}
                                    >
                                        <CustomTable
                                            columns={strikeColumns('').filter((col) => col.field === 'putOption')}
                                            rows={[]}
                                            keyFieldInData="key"
                                            className={classes.emptyHeader}
                                            disableColGroup
                                            tableContainerSxProps={{
                                                overflowX: 'auto',
                                                scrollbarWidth: 'none',
                                                msOverflowStyle: 'none',
                                                width: 'auto',
                                                '& th': {
                                                    whiteSpace: 'nowrap',
                                                    height: '30px !important',
                                                    padding: '0px !important'
                                                }
                                            }}
                                            tableSxProps={{
                                                tableLayout: 'auto !important'
                                            }}
                                        />
                                    </Box>
                                </Box>
                                <Box
                                    ref={itmBodyRef}
                                    sx={{
                                        flex: 1,
                                        '&::-webkit-scrollbar': { display: 'none' },
                                        overflowX: 'auto',
                                        overflowY: 'hidden',
                                        scrollbarWidth: 'none',
                                        msOverflowStyle: 'none',
                                        position: 'relative'
                                    }}
                                >
                                    <CustomTable
                                        hideHeader
                                        columns={expiryColumns.filter((col) => col.field === 'expiration')}
                                        rows={requiredOptionExpiries ?? []}
                                        RenderCollapsibleContent={OptionChainExpandedTable}
                                        collapseComponentProps={collapsePropsPutOption}
                                        expandedRows={disableRowCollapse ? requiredOptionExpiries?.map((_, i) => i.toString()) : expandedRows}
                                        toggleExpandedRow={toggleExpandedRow}
                                        sortField="id"
                                        verticalDividers
                                        keyFieldInData="date"
                                        className="extended-option-chain"
                                        tableContainerSxProps={{
                                            overflowX: 'auto',
                                            overflowY: 'hidden',
                                            scrollbarWidth: 'none',
                                            msOverflowStyle: 'none',
                                            width: 'auto'
                                        }}
                                        tableSxProps={{
                                            tableLayout: 'auto !important'
                                        }}
                                    />
                                </Box>
                            </Box>
                        </Box>
                    </Paper>
                </>
            )}

            {!showLoadingShimmer && selectedOptionChainTableView === OptionChainTableView.OTM && (
                <>
                    {' '}
                    <Paper className={`${classes.flex} ${classes.flexCol} ${classes.hFull}`} sx={{ borderRadius: 0, width: '100%', height: '100%' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'row', height: 'auto', width: '100%' }}>
                            <Box sx={{ flex: '0 0 calc((100%))', display: 'flex', flexDirection: 'column', maxWidth: 'calc((100%))' }}>
                                <Box
                                    sx={{
                                        position: 'sticky',
                                        top: 0,
                                        zIndex: 99,
                                        backgroundColor: (theme) => (theme.palette.mode === 'dark' ? '#252525' : '#F5F5F5')
                                    }}
                                >
                                    <Box
                                        ref={itmHeaderRef}
                                        sx={{
                                            overflowX: 'auto',
                                            overflowY: 'hidden',
                                            '&::-webkit-scrollbar': { display: 'none' },
                                            scrollbarWidth: 'none',
                                            msOverflowStyle: 'none',
                                            pointerEvents: 'none'
                                        }}
                                    >
                                        <CustomTable
                                            columns={strikeColumns('').filter((col) => col.field === 'otmOption')}
                                            rows={[]}
                                            keyFieldInData="key"
                                            className={classes.emptyHeader}
                                            disableColGroup
                                            tableContainerSxProps={{
                                                overflowX: 'auto',
                                                scrollbarWidth: 'none',
                                                msOverflowStyle: 'none',
                                                width: 'auto',
                                                '& th': {
                                                    whiteSpace: 'nowrap',
                                                    height: '30px !important',
                                                    padding: '0px !important'
                                                }
                                            }}
                                            tableSxProps={{
                                                tableLayout: 'auto !important'
                                            }}
                                        />
                                    </Box>
                                </Box>
                                <Box
                                    ref={itmBodyRef}
                                    sx={{
                                        flex: 1,
                                        '&::-webkit-scrollbar': { display: 'none' },
                                        overflowX: 'auto',
                                        overflowY: 'hidden',
                                        scrollbarWidth: 'none',
                                        msOverflowStyle: 'none',
                                        position: 'relative'
                                    }}
                                >
                                    <CustomTable
                                        hideHeader
                                        columns={expiryColumns.filter((col) => col.field === 'expiration')}
                                        rows={requiredOptionExpiries ?? []}
                                        RenderCollapsibleContent={OptionChainExpandedTable}
                                        collapseComponentProps={collapsePropsOtmOption}
                                        expandedRows={disableRowCollapse ? requiredOptionExpiries?.map((_, i) => i.toString()) : expandedRows}
                                        toggleExpandedRow={toggleExpandedRow}
                                        sortField="id"
                                        verticalDividers
                                        keyFieldInData="date"
                                        className="extended-option-chain"
                                        tableContainerSxProps={{
                                            overflowX: 'auto',
                                            overflowY: 'hidden',
                                            scrollbarWidth: 'none',
                                            msOverflowStyle: 'none',
                                            width: 'auto'
                                        }}
                                        tableSxProps={{
                                            tableLayout: 'auto !important'
                                        }}
                                    />
                                </Box>
                            </Box>
                        </Box>
                    </Paper>
                </>
            )}
            {!showLoadingShimmer && selectedOptionChainTableView === OptionChainTableView.ITM && (
                <>
                    {' '}
                    <Paper className={`${classes.flex} ${classes.flexCol} ${classes.hFull}`} sx={{ borderRadius: 0, width: '100%', height: '100%' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'row', height: 'auto', width: '100%' }}>
                            <Box sx={{ flex: '0 0 calc((100%))', display: 'flex', flexDirection: 'column', maxWidth: 'calc((100%))' }}>
                                <Box
                                    sx={{
                                        position: 'sticky',
                                        top: 0,
                                        zIndex: 99,
                                        backgroundColor: (theme) => (theme.palette.mode === 'dark' ? '#252525' : '#F5F5F5')
                                    }}
                                >
                                    <Box
                                        ref={itmHeaderRef}
                                        sx={{
                                            overflowX: 'auto',
                                            overflowY: 'hidden',
                                            '&::-webkit-scrollbar': { display: 'none' },
                                            scrollbarWidth: 'none',
                                            msOverflowStyle: 'none',
                                            pointerEvents: 'none'
                                        }}
                                    >
                                        <CustomTable
                                            columns={strikeColumns('').filter((col) => col.field === 'itmOption')}
                                            rows={[]}
                                            keyFieldInData="key"
                                            className={classes.emptyHeader}
                                            disableColGroup
                                            tableContainerSxProps={{
                                                overflowX: 'auto',
                                                scrollbarWidth: 'none',
                                                msOverflowStyle: 'none',
                                                width: 'auto',
                                                '& th': {
                                                    whiteSpace: 'nowrap',
                                                    height: '30px !important',
                                                    padding: '0px !important'
                                                }
                                            }}
                                            tableSxProps={{
                                                tableLayout: 'auto !important'
                                            }}
                                        />
                                    </Box>
                                </Box>
                                <Box
                                    ref={itmBodyRef}
                                    sx={{
                                        flex: 1,
                                        '&::-webkit-scrollbar': { display: 'none' },
                                        overflowX: 'auto',
                                        overflowY: 'hidden',
                                        scrollbarWidth: 'none',
                                        msOverflowStyle: 'none',
                                        position: 'relative'
                                    }}
                                >
                                    <CustomTable
                                        hideHeader
                                        columns={expiryColumns.filter((col) => col.field === 'expiration')}
                                        rows={requiredOptionExpiries ?? []}
                                        RenderCollapsibleContent={OptionChainExpandedTable}
                                        collapseComponentProps={collapsePropsItmOption}
                                        expandedRows={disableRowCollapse ? requiredOptionExpiries?.map((_, i) => i.toString()) : expandedRows}
                                        toggleExpandedRow={toggleExpandedRow}
                                        sortField="id"
                                        verticalDividers
                                        keyFieldInData="date"
                                        className="extended-option-chain"
                                        tableContainerSxProps={{
                                            overflowX: 'auto',
                                            overflowY: 'hidden',
                                            scrollbarWidth: 'none',
                                            msOverflowStyle: 'none',
                                            width: 'auto'
                                        }}
                                        tableSxProps={{
                                            tableLayout: 'auto !important'
                                        }}
                                    />
                                </Box>
                            </Box>
                        </Box>
                    </Paper>
                </>
            )}
        </Paper>
        </OptionChainPopoverProvider>
    )
}

export default memo(OptionChainTable)
